<script setup>
import { computed, onMounted, ref } from 'vue'
import { useQueueStore } from '@state/stores/queue'
import { usePlaybackStore } from '@state/stores/playback'
import DataLayer from '@services/data-layer/index'
import modalService from '@/services/modal-service'
import { useAppearanceStore } from '@state/stores/appearance'
import { useAuthStore } from '@state/stores/auth'
import { useQueueService } from '@composables/use-queue-service'

const PLAYLIST_PAGE_SIZE = 20

const props = defineProps({
    action: {
        type: Object,
        default: () => {},
    },
    title: {
        type: String,
        default: 'Learn more...',
    },
})

const appearanceStore = useAppearanceStore()
const authStore = useAuthStore()
const queueStore = useQueueStore()
const playbackStore = usePlaybackStore()
const queueService = useQueueService()

const playlist = ref({})
const playlistMixes = ref([])
const totalMixes = ref(null)

const loggedIn = computed(() => authStore.loggedIn)

const isActionType = type => !!props.action && props.action.type === type

const isPlayingNow = computed(() => {
    const { status } = playbackStore
    return status === 'playing' || status === 'loading'
})

const isChannelPlaying = computed(() => {
    if (!isActionType('playChannel')) return false
    const { resource } = playbackStore
    return isPlayingNow.value && resource && resource.type === 'channel' && resource.id === props.action.value
})

const isMixPlaying = computed(() => {
    if (!isActionType('playMix')) return false
    const { resource } = playbackStore
    return isPlayingNow.value && resource && resource.type === 'mix' && resource.id === props.action.value
})

const isMixLoaded = computed(() => {
    if (!isActionType('playMix')) return false
    const { resource } = playbackStore
    return resource && props.action.value === resource.id
})

const isCurrentPlaylistPlaying = computed(
    () => isActionType('playPlaylist') && Number(props.action.value) === queueStore.playlistId
)

function warn(error) {
    appearanceStore.handleNotification({ message: error.message, type: 'warning' })
}

function handleChannelClick() {
    if (isChannelPlaying.value) {
        queueService.pause()
        return
    }
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }
    queueService.playRadioChannel(props.action.value).catch(warn)
}

function handleMixClick() {
    if (isMixLoaded.value || isMixPlaying.value) {
        queueService.pause()
        return
    }
    if (!loggedIn.value) {
        modalService.show('AUTH_REQUIRED_FOR_PLAYBACK')
        return
    }
    DataLayer.mixes.getById(props.action.value).then(({ body: mix }) => {
        queueService.playMix(mix).catch(warn)
    })
}

function playPlaylist() {
    if (isCurrentPlaylistPlaying.value) {
        queueService.pause()
        return
    }
    queueService.playMixMultiple({
        mixes: [...playlistMixes.value],
        id: playlist.value.id,
        position: 0,
    })
}

function getPlaylistMixesAndPlay({ playlistId = props.action.value, limit = PLAYLIST_PAGE_SIZE, offset = 0 }) {
    if (totalMixes.value === playlistMixes.value.length) {
        playPlaylist()
        return
    }
    DataLayer.playlists.getRevisionsMixes(playlistId, limit, offset).then(mixes => {
        totalMixes.value = Number(mixes.headers['x-items-total'])
        playlistMixes.value = mixes.body.map(revisionsMixes => revisionsMixes.mixes)
        getPlaylistMixesAndPlay({ limit: totalMixes.value })
    })
}

onMounted(() => {
    if (isActionType('playPlaylist')) {
        DataLayer.playlists.getById(props.action.value).then(response => {
            playlist.value = response.body
        })
    }
})
</script>

<template>
    <div v-if="action">
        <button
            v-if="action.type === 'playChannel'"
            class="btn"
            :title="isChannelPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isChannelPlaying ? 'Pause' : 'Listen Now'"
            @click="handleChannelClick"
        >{{ isChannelPlaying ? 'Pause' : 'Listen Now' }}</button>
        <button
            v-if="action.type === 'playMix'"
            class="btn"
            :title="isMixPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isMixPlaying ? 'Pause' : 'Listen Now'"
            @click="handleMixClick"
        >{{ isMixPlaying ? 'Pause' : 'Listen Now' }}</button>
        <button
            v-if="action.type === 'playPlaylist'"
            class="btn"
            :title="isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now'"
            :aria-label="isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now'"
            @click="getPlaylistMixesAndPlay({})"
        >{{ isCurrentPlaylistPlaying ? 'Pause' : 'Listen Now' }}</button>

        <BaseLink
            v-if="action.type === 'navigateInternal'"
            :title="title"
            :aria-label="title"
            :to="{ path: action.value }"
        >
            <button
                class="btn"
                :title="title"
                :aria-label="title"
            >Learn More</button>
        </BaseLink>
        <BaseLink
            v-if="action.type === 'navigateExternal'"
            :href="action.value"
        >
            <button
                class="btn"
                :title="title"
                :aria-label="title"
            >Learn More</button>
        </BaseLink>
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: $s-xs $s-m;
    font-family: $system-default-font-family;
    font-size: 12px;
    font-weight: 700;
    color: $c-black;
    letter-spacing: 0.5px;
    cursor: pointer;
    background-color: $c-white;
    border: none;
    border-radius: $radius-16;

    @include transition(opacity);

    &:hover {
        opacity: 0.85;
    }
}
</style>
